import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AppChromeTop, AppChromeBottom } from "@/components/AppChrome";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import MetaPixel from "@/components/MetaPixel";
import OAuthErrorHandler from "@/components/OAuthErrorHandler";
import PageTransition from "@/components/PageTransition";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import AdminLockGuard from "@/components/AdminLockGuard";
import LessonNotesSync from "@/components/LessonNotesSync";
import Index from "./pages/Index";
import Lp from "./pages/Lp";
import { AiAssistantProvider } from "@/contexts/AiAssistantContext";
import { Suspense } from "react";
import { lazyPage } from "@/lib/lazyPage";

// Every page except the two entry points people land on from ads and search
// (the homepage and /lp) is its own chunk, so a first visit downloads only the
// page it asked for instead of the whole site, admin panel included.
const AdminLayout = lazyPage(() => import("@/components/admin/AdminLayout"));
const AdminDashboard = lazyPage(() => import("./pages/admin/AdminDashboard"));
const AdminCourses = lazyPage(() => import("./pages/admin/AdminCourses"));
const AdminLessons = lazyPage(() => import("./pages/admin/AdminLessons"));
const AdminArticles = lazyPage(() => import("./pages/admin/AdminArticles"));
const AdminKnowledge = lazyPage(() => import("./pages/admin/AdminKnowledge"));
const AdminNews = lazyPage(() => import("./pages/admin/AdminNews"));
const AdminTestimonials = lazyPage(() => import("./pages/admin/AdminTestimonials"));
const AdminLeads = lazyPage(() => import("./pages/admin/AdminLeads"));
const AdminMessages = lazyPage(() => import("./pages/admin/AdminMessages"));
const AdminStudents = lazyPage(() => import("./pages/admin/AdminStudents"));
const AdminStudentProfile = lazyPage(() => import("./pages/admin/AdminStudentProfile"));
const AdminUsers = lazyPage(() => import("./pages/admin/AdminUsers"));
const AdminPayments = lazyPage(() => import("./pages/admin/AdminPayments"));
const AdminPricing = lazyPage(() => import("./pages/admin/AdminPricing"));
const AdminAuditLog = lazyPage(() => import("./pages/admin/AdminAuditLog"));
const AdminSettings = lazyPage(() => import("./pages/admin/AdminSettings"));
const AdminGuide = lazyPage(() => import("./pages/admin/AdminGuide"));
const Llp = lazyPage(() => import("./pages/Llp"));
const ThankYou = lazyPage(() => import("./pages/ThankYou"));
const StudentStories = lazyPage(() => import("./pages/StudentStories"));
const Login = lazyPage(() => import("./pages/Login"));
const Register = lazyPage(() => import("./pages/Register"));
const ForgotPassword = lazyPage(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const ConfirmEmail = lazyPage(() => import("./pages/ConfirmEmail"));
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const Courses = lazyPage(() => import("./pages/Courses"));
const Enroll = lazyPage(() => import("./pages/Enroll"));
const EnrollForm = lazyPage(() => import("./pages/EnrollForm"));
const About = lazyPage(() => import("./pages/About"));
const Contact = lazyPage(() => import("./pages/Contact"));
const Insights = lazyPage(() => import("./pages/Insights"));
const AiAssistant = lazyPage(() => import("./pages/AiAssistant"));
const News = lazyPage(() => import("./pages/News"));
const Lesson = lazyPage(() => import("./pages/Lesson"));
const Privacy = lazyPage(() => import("./pages/Privacy"));
const Terms = lazyPage(() => import("./pages/Terms"));
const Aml = lazyPage(() => import("./pages/Aml"));
const LearningExperience = lazyPage(() => import("./pages/LearningExperience"));
const CourseOverview = lazyPage(() => import("./pages/CourseOverview"));
const Payment = lazyPage(() => import("./pages/Payment"));
const ArticleDetail = lazyPage(() => import("./pages/ArticleDetail"));
const Pricing = lazyPage(() => import("./pages/Pricing"));
const Curriculum = lazyPage(() => import("./pages/Curriculum"));
const Testimonials = lazyPage(() => import("./pages/Testimonials"));
const CoursePlayer = lazyPage(() => import("./pages/CoursePlayer"));
const NotFound = lazyPage(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        {/* Meta Pixel, ID 1728861491525973: mounted once here so it covers
            every route, and fires PageView on every client-side navigation
            (see src/components/MetaPixel.tsx for why that needs its own
            listener in an SPA). */}
        <MetaPixel />
        <OAuthErrorHandler />
        <AuthProvider>
          <AdminLockGuard />
          <LessonNotesSync />
          <AiAssistantProvider>
          <ErrorBoundary>
            <AppChromeTop />
            <PageTransition>
            {/* While a page's chunk downloads, the space it will fill stays reserved.
                with an empty fallback the footer sat at the top of the screen and
                was pushed down by the whole page when it arrived (a layout shift
                of 0.8 on /about and /pricing). */}
            <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              {/* Standalone campaign landing page: no navbar, breadcrumbs or footer
                  (see src/lib/chromeless.ts), and noindex. */}
              <Route path="/lp" element={<Lp />} />
              <Route path="/llp" element={<Llp />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* Capitalised on purpose: that is the address campaigns link to. The
                  router matches case-insensitively; isChromelessRoute lowercases. */}
              <Route path="/Student-Stories" element={<StudentStories />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/confirm-email" element={<ConfirmEmail />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/enroll" element={<Enroll />} />
              <Route path="/enroll-form" element={<EnrollForm />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/ai-assistant" element={<AiAssistant />} />
              <Route path="/news" element={<News />} />
              <Route path="/lesson/:id" element={<Lesson />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/aml" element={<Aml />} />
              <Route path="/learning-experience" element={<LearningExperience />} />
              <Route path="/course-overview/:courseId" element={<CourseOverview />} />
              <Route path="/insights/:slug" element={<ArticleDetail />} />
              <Route path="/payment" element={<Payment />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/curriculum" element={<Curriculum />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/play/:courseId" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
              <Route path="/play/:courseId/:lessonId" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />

              {/* Admin routes — guarded by AdminRoute (RBAC) */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="courses" element={<AdminCourses />} />
                <Route path="courses/:id/lessons" element={<AdminLessons />} />
                <Route path="articles" element={<AdminArticles />} />
                <Route path="knowledge" element={<AdminKnowledge />} />
                <Route path="news" element={<AdminNews />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="students" element={<AdminStudents />} />
                <Route path="students/:userId" element={<AdminStudentProfile />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="audit" element={<AdminAuditLog />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="guide" element={<AdminGuide />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </PageTransition>
            <Footer />
            <AppChromeBottom />
          </ErrorBoundary>
          </AiAssistantProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
